import { useSafeAreaPadding } from '@/hooks/useSafeAreaPadding';
import { Ionicons } from '@expo/vector-icons';
import { ScrollView } from 'react-native';
import { Adapt, Sheet, Select as TamaguiSelect } from 'tamagui';
import { Grabber } from '../Grabber';
import type { SelectProps } from './select.types';
import { useEffect, useState } from 'react';

/**
 * A reusable Select component built with Tamagui, providing a dropdown for selecting options.
 * Supports cross-platform rendering with adaptive behavior for touch devices.
 */
export function Select({
  options,
  value,
  onValueChange,
  placeholder,
  triggerSize = '$3',
  snapPoints = undefined,
}: SelectProps): React.ReactNode {
  const [selectedValue, setSelectedValue] = useState(value);
  const [open, setOpen] = useState(false);
  const padding = useSafeAreaPadding();

  const handleOpen = (): void => {
    setOpen(!open);
    if (options.length === 1) {
      setSelectedValue(options[0].value);
      handleValueChange(options[0].value);
    }
  };

  const handleValueChange = (value: string): void => {
    onValueChange(value);
    setOpen(false);
  };

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <TamaguiSelect
      value={selectedValue}
      onValueChange={handleValueChange}
      onOpenChange={handleOpen}
      open={open}
    >
      <TamaguiSelect.Trigger
        backgroundColor="$tertiary50"
        borderWidth={0}
        iconAfter={
          selectedValue && options.length === 1 ? (
            <Ionicons name="checkmark" />
          ) : (
            <Ionicons name="chevron-down" />
          )
        }
        size={triggerSize}
        borderRadius="$true"
      >
        <TamaguiSelect.Value placeholder={placeholder} />
      </TamaguiSelect.Trigger>

      <Adapt when="sm" platform="touch">
        <Sheet
          modal
          dismissOnSnapToBottom
          dismissOnOverlayPress
          snapPointsMode={snapPoints ? 'percent' : 'fit'}
          snapPoints={snapPoints}
        >
          <Sheet.Frame
            paddingBottom={padding.bottomPadding}
            paddingTop="$true"
            borderRadius="$modal"
          >
            <Grabber />
            <ScrollView
              keyboardShouldPersistTaps="always"
              nestedScrollEnabled
              contentContainerStyle={{ flexGrow: 1 }}
            >
              <Adapt.Contents />
            </ScrollView>
          </Sheet.Frame>
          <Sheet.Overlay backgroundColor="$shadow" />
        </Sheet>
      </Adapt>

      <TamaguiSelect.Content zIndex={10000}>
        <TamaguiSelect.ScrollUpButton />
        <TamaguiSelect.Viewport
          minWidth={200}
          maxHeight={240}
          overflow="scroll"
        >
          <TamaguiSelect.Group>
            {options.map((option, index) => (
              <TamaguiSelect.Item
                key={option.value}
                index={index}
                value={option.value}
              >
                <TamaguiSelect.ItemText>{option.label}</TamaguiSelect.ItemText>
                <TamaguiSelect.ItemIndicator>
                  <Ionicons name="checkmark" />
                </TamaguiSelect.ItemIndicator>
              </TamaguiSelect.Item>
            ))}
          </TamaguiSelect.Group>
        </TamaguiSelect.Viewport>
        <TamaguiSelect.ScrollDownButton />
      </TamaguiSelect.Content>
    </TamaguiSelect>
  );
}
